import actionTypes from './actionTypes';
import enforceNotificationStructure from 'src/utils/dataStructure/enforceNotificationStructure';
import api, { createAuthenticatedApiInstance } from 'src/utils/api';
import { setAlert } from 'src/typescript/components/alert/actions';
import i18n from 'src/utils/translate';
import { error as logError } from 'loglevel';
import { getLocalStorageItem, Key } from 'src/utils/localStorage';
import { closeModal } from '../modal/actions';
import { emptyGuidelines } from 'src/constants/defaultCommunityGuidelines';
import { POST_URL } from 'src/constants/regex';
import router from 'next/router';
import { getDismissedFeaturedPostIds, formatFeaturedPosts } from 'src/utils/featuredPosts';

export const fetchCurrentUser = () => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.FETCH_CURRENT_USER });
    try {
      const api = createAuthenticatedApiInstance(getState);
      const response = await api.get('private/user/current');
      return dispatch({
        type: actionTypes.FETCH_CURRENT_USER_SUCCESS,
        user: response.data,
      });
    } catch (ex) {
      logError(ex);
      dispatch({ type: actionTypes.FETCH_CURRENT_USER_FAILURE });
    }
  };
};

export const fetchCurrentCommunity = (slug) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.FETCH_CURRENT_COMMUNITY });
    try {
      const state = getState();
      const currentUser = state.context.currentUser;
      const isMember = !!currentUser
        && !!currentUser.followedCommunities.find(x => x.slug === slug);
      const api = createAuthenticatedApiInstance(getState);
      const encodedSlug = encodeURI(slug);
      const { data } = !isMember
        ? await api.getPublic(`communities/${encodedSlug}`)
        : await api.get(`private/communities/${encodedSlug}`);
      return dispatch({
        type: actionTypes.FETCH_CURRENT_COMMUNITY_SUCCESS,
        community: data,
      });
    } catch (ex) {
      const statusCode = ex.response && ex.response.status
        ? ex.response.status
        : null;

      if (statusCode !== 314) { // temporary hack to avoid logging when redirecting to BG
        logError(ex);
      }

      return dispatch({
        type: actionTypes.FETCH_CURRENT_COMMUNITY_FAILURE,
        statusCode,
      });
    }
  };
};

export const clearCurrentCommunity = () => {
  return (dispatch) => {
    dispatch({ type: actionTypes.CLEAR_CURRENT_COMMUNITY });
  };
};

export const fetchPermissions = (communityId) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.FETCH_PERMISSIONS });
    try {
      const { context: { currentUser } } = getState();
      const api = createAuthenticatedApiInstance(getState);
      const { data: { permissions, roles } } = await api.get(`private/users/${currentUser.userId}/permissions`, {
        params: { communityId },
      });
      dispatch({
        type: actionTypes.FETCH_PERMISSIONS_SUCCESS,
        permissions,
        roles,
      });
    } catch (ex) {
      logError(ex);
      dispatch({ type: actionTypes.FETCH_PERMISSIONS_FAILURE });
    }
  };
};

export const fetchUnreadMessagesCount = () => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.FETCH_UNREAD_MESSAGES_COUNT });

    try {
      const response = await api.auroraGet('messages/unread-count');
      const { data: { payload: { unreadMessages } = {}, status } } = response;

      if (status && !status.success) {
        return dispatch({ type: actionTypes.FETCH_UNREAD_MESSAGES_COUNT_FAILURE });
      }

      dispatch({ type: actionTypes.FETCH_UNREAD_MESSAGES_COUNT_SUCCESS, count: unreadMessages });
    } catch (ex) {
      logError(ex);
      dispatch({ type: actionTypes.FETCH_UNREAD_MESSAGES_COUNT_FAILURE });
    }
  };
};

export const fetchUnreadNotificationsCount = () => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.FETCH_UNREAD_NOTIFICATIONS_COUNT });

    try {
      const response = await api.auroraGet('notifications/unread');
      const { data: { payload: { unread } = {}, status } } = response;

      if (status && !status.success) {
        return dispatch({ type: actionTypes.FETCH_UNREAD_NOTIFICATIONS_COUNT_FAILURE });
      }

      dispatch({ type: actionTypes.FETCH_UNREAD_NOTIFICATIONS_COUNT_SUCCESS, count: unread });
    } catch (ex) {
      logError(ex);
      dispatch({ type: actionTypes.FETCH_UNREAD_NOTIFICATIONS_COUNT_FAILURE });
    }
  };
};

export const fetchNotifications = () => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.FETCH_NOTIFICATIONS });

    try {
      const response = await api.auroraGet('notifications/items', {
        params: {
          size: 5,
          markread: true,
          from: 0,
        },
      });
      const { data: { payload: { notifications: notificationData, users, groups } = {}, status } } = response;

      if (status && !status.success) {
        return dispatch({ type: actionTypes.FETCH_NOTIFICATIONS_FAILURE });
      }

      const notifications = notificationData.map(n => enforceNotificationStructure(n, users, groups));
      dispatch({ type: actionTypes.FETCH_NOTIFICATIONS_SUCCESS, notifications });
    } catch (ex) {
      logError(ex);
      dispatch({ type: actionTypes.FETCH_NOTIFICATIONS_FAILURE });
    }
  };
};

export const updateLanguage = (lang) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.UPDATE_LANGUAGE });
    try {
      const state = getState();
      const currentUser = state.context.currentUser;
      if (currentUser) {
        const response = await api.auroraPut('account/language', { userId: currentUser.userId, lang });
        const { data: { status } } = response;

        if (status && !status.success) {
          dispatch(setAlert(status.message, 'danger', false));
          return dispatch({ type: actionTypes.UPDATE_LANGUAGE_FAILURE });
        }
      }

      dispatch({ type: actionTypes.UPDATE_LANGUAGE_SUCCESS });
    } catch (ex) {
      logError(ex);
      dispatch({ type: actionTypes.UPDATE_LANGUAGE_FAILURE });
    }
  };
};

export const resendVerificationLink = () => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.RESEND_VERIFICATION_LINK });
    try {
      await api.auroraPost('profile/resend-verification-link');
      dispatch(setAlert(i18n.t('Verification email sent!'), 'success'));
      dispatch({ type: actionTypes.RESEND_VERIFICATION_LINK_SUCCESS });
    } catch (ex) {
      logError(ex);
      dispatch({ type: actionTypes.RESEND_VERIFICATION_LINK_FAILURE });
    }
  };
};

export const resetFloatingSignupBox = () => {
  return (dispatch) => {
    dispatch({ type: actionTypes.RESET_FLOATING_SIGNUP_BOX });
  };
};

export const hideFloatingSignupBox = () => {
  return (dispatch) => {
    dispatch({ type: actionTypes.HIDE_FLOATING_SIGNUP_BOX });
  };
};

export const fetchGuidelines = (communitySlug, isPrivate = false) => async (dispatch) => {
  try {
    if (!communitySlug) {
      throw Error('fetchGuidelines: missing community-slug');
    }
    const response = isPrivate
      ? await api.get(`private/communities/${communitySlug}/guidelines`)
      : await api.getPublic(`communities/${communitySlug}/guidelines`);
    if (response.data) {
      dispatch({
        type: actionTypes.SET_COMMUNITY_GUIDELINES,
        slug: communitySlug,
        extendedGuidelinesUrl: response.data.extendedGuidelines,
        guidelines: response.data.guidelines,
      });
    } else {
      throw Error('fetchGuidelines: bad response');
    }
  } catch (ex) {
    logError(ex);
  }
};

export const fetchGuidelinesOnce = (communitySlug, isPrivate) => async (dispatch, getState) => {
  const { context: { currentCommunity } } = getState();

  // don't fetch if user has dismissed guidelines
  const guidelinesDismissed = getLocalStorageItem(Key.guidelinesDismissed);
  if (guidelinesDismissed) {
    const communityIds = JSON.parse(guidelinesDismissed);
    if (Array.isArray(communityIds) && communityIds.includes(currentCommunity.communityId)) {
      return;
    }
  }

  // fetch if no guidelines or new community
  if (currentCommunity && (!currentCommunity.guidelines || currentCommunity.slug !== communitySlug)) {
    dispatch(fetchGuidelines(communitySlug, isPrivate));
  }
};

export const updateGuidelines = (communitySlug, guidelines = emptyGuidelines) => async (dispatch, getState) => {
  const api = createAuthenticatedApiInstance(getState);
  try {
    if (!communitySlug) {
      throw Error('updateGuidelines: missing community-slug');
    }
    const extendedGuidelinesTitleRemoved = guidelines.extendedGuidelinesUrl.replace(POST_URL, '$1');
    const response = await api.post(`private/communities/${communitySlug}/guidelines`, {
      extendedGuidelines: extendedGuidelinesTitleRemoved,
      guidelines: guidelines.guidelines,
    });
    if (response.data) {
      dispatch({
        type: actionTypes.SET_COMMUNITY_GUIDELINES,
        slug: communitySlug,
        extendedGuidelinesUrl: response.data.extendedGuidelines,
        guidelines: response.data.guidelines,
      });
      dispatch(closeModal());
    } else {
      throw Error('updateGuidelines: bad response');
    }
  } catch (ex) {
    logError(ex);
    dispatch(setAlert(i18n.t('Error updating guidelines')));
  }
};

export const fetchCurrentCommunityRoles = ({communityId, slug}, showRestrictiveRoles, aurora = true) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.FETCH_CURRENT_COMMUNITY_ROLES });
    try {
      if (aurora) { // TODO: Remove Aurora action
        const response = await api.auroraGet('roles', {
          params: {
            groupId: communityId,
            groupCode: slug,
            showRestrictiveRoles },
        });
        const { data: { payload: { roles } = {}, status } } = response;

        if (status && !status.success) {
          return dispatch({ type: actionTypes.FETCH_CURRENT_COMMUNITY_ROLES_FAILURE });
        }

        dispatch({
          type: actionTypes.FETCH_CURRENT_COMMUNITY_ROLES_SUCCESS,
          roles: roles.filter(r => r.id !== 4), // exclude HealthUnlocked badge
        });
      } else {
        const api = createAuthenticatedApiInstance(getState);
        const response = await api.get(`private/communities/${slug}/badges`);
        if (response.status === 200 && response.data) {
          dispatch({
            type: actionTypes.FETCH_CURRENT_COMMUNITY_ROLES_SUCCESS,
            roles: response.data,
          });
        } else {
          dispatch(setAlert(i18n.t('Error fetching community badges, please try again.')));
          dispatch(closeModal());
        }
      }
    } catch (ex) {
      dispatch({ type: actionTypes.FETCH_CURRENT_COMMUNITY_ROLES_FAILURE });
      dispatch(setAlert(i18n.t('Error fetching community badges, please try again.')));
      dispatch(closeModal());
      logError('fetchCurrentCommunityRoles', ex);
    }
  };
};

export const updateMemberRoles = (slug, userId, roles, username) => async (dispatch, getState) => {
  dispatch({ type: actionTypes.UPDATE_MEMBER_ROLES });
  try {
    const api = createAuthenticatedApiInstance(getState);
    const response = await api.post(`private/members/${slug}/badges`, {userId, roles});
    if (response.status === 200) {
      const roleString = response.data.roles.map(({roleName}) => roleName).join(', ');
      const successMessage = roleString
        ? i18n.t(`${username} has been assigned the %1$s badge.`).replace('%1$s', roleString)
        : i18n.t(`${username} has had all badges removed.`);
      dispatch(setAlert(successMessage, 'success'));
      dispatch(closeModal());
      // UPDATE_MEMBER_ROLES_SUCCESS does not update the store
      // but may be useful if we need to do client side updates of member badges in the future
      dispatch({ type: actionTypes.UPDATE_MEMBER_ROLES_SUCCESS });
      setTimeout(() => router.replace(window.location.pathname + window.location.search + window.location.hash), 500);
    } else {
      dispatch(setAlert(i18n.t('Error updating member\'s badges, please try again.')));
      dispatch({ type: actionTypes.UPDATE_MEMBER_ROLES_FAILURE });
    }
  } catch (ex) {
    logError('updateMemberRoles', ex);
    dispatch(setAlert(i18n.t('Error updating member\'s badges, please try again.')));
    dispatch({ type: actionTypes.UPDATE_MEMBER_ROLES_FAILURE });
  }
};

export const fetchFeaturedPosts = (params) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.FETCH_FEATURED_POSTS });
    try {
      const api = createAuthenticatedApiInstance(getState);
      const response = await api.getPublic('featured-posts', { params });
      const { data: featuredPosts, status } = response;
      if (status !== 200) {
        dispatch({ type: actionTypes.FETCH_FEATURED_POSTS_FAILURE });
        return;
      }

      dispatch({
        type: actionTypes.FETCH_FEATURED_POSTS_SUCCESS,
        featuredPosts: formatFeaturedPosts(featuredPosts.map(x => ({ ...x, id: x.featuredPostId }))),
        dismissedFeaturedPostIds: getDismissedFeaturedPostIds(),
      });
    } catch (ex) {
      logError(ex);
      dispatch({ type: actionTypes.FETCH_FEATURED_POSTS_FAILURE });
    }
  };
};

export const dismissFeaturedPost = () => {
  return {
    type: actionTypes.DISMISS_FEATURED_POST_SUCCESS,
    dismissedFeaturedPostIds: getDismissedFeaturedPostIds(),
  };
};
